// Pure helpers for EV/Hybrid efficiency calculations.
// All inputs/outputs are in metric (km, kWh). Display layer converts to user units.

export function deriveDistanceKm(start, end) {
  const s = parseFloat(start);
  const e = parseFloat(end);
  if (!isFinite(s) || !isFinite(e)) return null;
  const d = e - s;
  return d > 0 ? d : null;
}

export function deriveEnergyUsedKwh(startPct, endPct, capacityKwh) {
  const s = parseFloat(startPct);
  const e = parseFloat(endPct);
  const cap = parseFloat(capacityKwh);
  if (!isFinite(s) || !isFinite(e) || !isFinite(cap) || cap <= 0) return null;
  const delta = s - e; // for trips: discharged
  if (delta <= 0) return null;
  return (delta / 100) * cap;
}

export function deriveChargeKwh(startPct, endPct, capacityKwh) {
  const s = parseFloat(startPct);
  const e = parseFloat(endPct);
  const cap = parseFloat(capacityKwh);
  if (!isFinite(s) || !isFinite(e) || !isFinite(cap) || cap <= 0) return null;
  const delta = e - s; // for charge: gained
  if (delta <= 0) return null;
  return (delta / 100) * cap;
}

export function deriveEfficiency(distanceKm, energyUsedKwh) {
  const d = parseFloat(distanceKm);
  const e = parseFloat(energyUsedKwh);
  if (!isFinite(d) || !isFinite(e) || d <= 0 || e <= 0) return null;
  return {
    kmPerKwh: d / e,
    whPerKm: (e * 1000) / d,
  };
}

export function deriveFuelUsedL(startPct, endPct, capacityL) {
  const s = parseFloat(startPct);
  const e = parseFloat(endPct);
  const cap = parseFloat(capacityL);
  if (!isFinite(s) || !isFinite(e) || !isFinite(cap) || cap <= 0) return null;
  const delta = s - e; // for trips: consumed
  if (delta <= 0) return null;
  return (delta / 100) * cap;
}

export function deriveLper100km(distanceKm, fuelL) {
  const d = parseFloat(distanceKm);
  const f = parseFloat(fuelL);
  if (!isFinite(d) || !isFinite(f) || d <= 0 || f <= 0) return null;
  return (f / d) * 100;
}

export function fmtEfficiency(trip, unit = "km") {
  // EV / Hybrid path
  const km = trip?.efficiency_km_per_kwh;
  const wh = trip?.efficiency_wh_per_km;
  // Gas / Hybrid path
  const l100 = trip?.efficiency_l_per_100km;

  // Prefer EV display if available
  if (km != null || wh != null) {
    if (unit === "mi") {
      const mpk = km != null ? km / 1.60934 : null;
      const whpm = wh != null ? wh * 1.60934 : null;
      return {
        kind: "ev",
        primary: mpk != null ? `${mpk.toFixed(1)} mi/kWh` : null,
        secondary: whpm != null ? `${whpm.toFixed(0)} Wh/mi` : null,
      };
    }
    return {
      kind: "ev",
      primary: km != null ? `${km.toFixed(1)} km/kWh` : null,
      secondary: wh != null ? `${wh.toFixed(0)} Wh/km` : null,
    };
  }

  if (l100 != null) {
    if (unit === "mi") {
      // L/100km → MPG (US): MPG = 235.215 / (L/100km)
      const mpg = l100 > 0 ? 235.215 / l100 : null;
      return {
        kind: "fuel",
        primary: mpg != null ? `${mpg.toFixed(1)} mpg` : null,
        secondary: `${l100.toFixed(1)} L/100km`,
      };
    }
    return {
      kind: "fuel",
      primary: `${l100.toFixed(1)} L/100km`,
      secondary: null,
    };
  }

  return null;
}
